<template>
  <v-card
    flat
    :to="isDisabled ? null : resolvedTo"
    :href="isDisabled ? null : resolvedHref"
    :target="settings.openNewTab ? '_blank' : null"
    :class="[
      'feature-tile',
      `tile-${settings.color || 'blue'}`,
      { 'is-disabled': isDisabled }
    ]"
    :style="tileStyle"
    :ripple="!isDisabled"
    @click="onClick"
  >
    <div class="icon-wrap">
      <v-icon size="64" :color="settings.iconColor || defaultIconColor">
        {{ settings.icon }}
      </v-icon>
    </div>
    <h3 class="tile-title">{{ settings.title }}</h3>
    <p class="tile-description">{{ settings.description }}</p>
    <v-icon v-if="isDisabled" class="lock-icon" size="16">lock_outline</v-icon>
  </v-card>
</template>

<script>
/**
 * A single pastel-coloured feature tile.
 *
 * Dual-mode:
 *  - Standalone: pass `title`, `description`, `icon`, `color`, `to` / `href` directly.
 *  - Landing-config: pass a `definition` object — all the same keys are read from
 *    it (same shape as a link entry on a landing-page row). The `definition` wins
 *    over direct props when both are provided.
 *
 * Supports `type: 'createdoc'` to emit `addDocument` instead of navigating
 * (mirrors the existing landing-page link semantics).
 */
export default {
  name: "cFeatureTile",
  props: {
    // landing-config mode
    definition: { type: Object, default: null },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
    // standalone mode
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    icon: { type: String, default: "" },
    color: { type: String, default: "blue" },
    iconColor: { type: String, default: null },
    to: { type: [String, Object], default: null },
    href: { type: String, default: null },
    openNewTab: { type: Boolean, default: false },
    type: { type: String, default: "link" }, // link | createdoc | ssolink
    createDocType: { type: String, default: null },
    /** Explicit height — CSS value (e.g. "180px", "12rem", "100%"). */
    height: { type: [String, Number], default: null },
  },
  computed: {
    settings() {
      const d = this.definition || {};
      return {
        title: d.title ?? this.title,
        description: d.description ?? this.description,
        icon: d.icon ?? this.icon,
        color: d.color ?? d.tile_color ?? this.color,
        iconColor: d.iconColor ?? d.icon_colour ?? this.iconColor,
        to: d.to ?? (d.url && !this.isExternal(d.url) ? d.url : null) ?? this.to,
        href: d.href ?? (d.url && this.isExternal(d.url) ? d.url : null) ?? this.href,
        openNewTab: d.open_newtab ?? d.openNewTab ?? this.openNewTab,
        type: d.type ?? this.type,
        createDocType: d.createDocType ?? this.createDocType,
        height: d.height ?? this.height,
      };
    },
    tileStyle() {
      const h = this.settings.height;
      if (h == null || h === "") return null;
      // bare numbers → px; otherwise trust the caller's CSS unit
      return { height: typeof h === "number" || /^\d+$/.test(String(h)) ? `${h}px` : h };
    },
    /**
     * Route-access resolution runs in vLandingHolder and sets `isAccessible`
     * on the link. Only the explicit `false` disables — undefined (e.g. in
     * standalone usage) is treated as accessible.
     */
    isDisabled() {
      const d = this.definition;
      return d != null && d.isAccessible === false;
    },
    defaultIconColor() {
      // darker accent matching the pastel bg
      const map = {
        blue: "#2B9DBF",
        yellow: "#D4A400",
        pink: "#EA3785",
        orange: "#ED742D",
        green: "#2cd946",
        purple: "#6938b9",
      };
      return map[this.settings.color] || "#2B9DBF";
    },
    resolvedTo() {
      if (this.settings.type === "createdoc") return null;
      return this.settings.to || null;
    },
    resolvedHref() {
      if (this.settings.type === "createdoc") return null;
      if (this.resolvedTo) return null;
      return this.settings.href || null;
    },
  },
  methods: {
    isExternal(url) {
      if (!url) return false;
      return /^https?:\/\//i.test(url);
    },
    onClick(e) {
      if (this.isDisabled) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        return;
      }
      if (this.settings.type === "createdoc") {
        e?.preventDefault?.();
        this.$emit("addDocument", this.settings.createDocType);
        return;
      }
      this.$emit("click", e);
    },
  },
};
</script>

<style scoped lang="scss">
.feature-tile {
  font-family: 'DM Sans', sans-serif !important;
  // Force all non-icon descendants to inherit DM Sans. .v-icon is excluded so
  // Vuetify's Material Icons / MDI font stays intact (icons are font-rendered).
  ::v-deep *:not(.v-icon):not(.v-icon *) { font-family: inherit !important; }
  h1, h2, ::v-deep h1, ::v-deep h2 {
    font-family: 'DM Serif Display', serif !important;
  }

  border-radius: 20px !important;
  padding: 24px 20px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08) !important;
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.55;
    // Neutralise the hover lift so disabled tiles don't react to pointer.
    &:hover {
      transform: none;
      box-shadow: none !important;
    }
  }

  .lock-icon {
    position: absolute;
    top: 12px;
    right: 12px;
    color: #6b7280;
    opacity: 0.6;
  }

  .icon-wrap {
    width: 56px;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tile-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #1f2937;
  }

  .tile-description {
    font-size: 14px;
    line-height: 1.4;
    color: #4b5563;
    margin: 0;
    flex: 1;
  }
}

.tile-blue   { background-color: #D7EEF5; }
.tile-yellow { background-color: #FEF3C2; }
.tile-pink   { background-color: #FDD6DF; }
.tile-orange { background-color: #FFE0C7; }
.tile-green  { background-color: #D6F2E0; }
.tile-purple { background-color: #E4DAF4; }
</style>
